import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import {
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  Grid,
  Box,
  Typography,
  Alert,
  Skeleton,
  Chip,
} from "@mui/material";
import { VCC_PROVIDERS, WhiteSecurityIcon } from "../common/constants";
import { useTranslation } from "react-i18next";
import {
  selectCurrencySymbol,
  setLatestPaymentMethod,
} from "../redux/slices/paymentMethodsSlice";
import { useAPI } from "../api/api";
import paymentService from "../api/paymentService";
import {
  AvailablePaymentMethod,
  PaymentMethodsResponse,
  PaymentType,
  VirtualCardResponse,
} from "../types/paymentTypes";
import { setCardData } from "../redux/slices/vccSlice";
import { CardData, TransformedVccCardType } from "../types/common";
import useDecrypt from "../common/useDecrypt";
import { useNavigate, useLocation } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { getVirtualCard } from "../api/paymentMethodsService";
import { showError, showToast } from "../redux/slices/toastSlice";
import { Payment } from "../types/stipendTypes";
import { paymentsService } from "../api/stipendsServices";
import { useErrorBoundary } from "react-error-boundary";
import { AxiosError } from "axios";
import { ErrorType, getErrorDetails } from "../common/CustomErrorBoundary";
import StorageManager from "../services/storage";
import { getMethodProviderName, formatCurrency } from "../common/helpers";
import {
  DeprecatedPaymentMethodStatus,
  getDeprecatedPaymentMethodStatus,
} from "../common/DeprecatedPaymentMethod";

const PaymentMethods: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const failedPayment = searchParams.get("failedPayment") === "true";
  let client = process.env.REACT_APP_CLIENT?.toLowerCase() ?? "velocity";

  const paymentMethodsSourceData = useSelector(
    (state: RootState) => state.paymentMethods.data
  ) as PaymentMethodsResponse;

  const paymentMethodsData =
    paymentMethodsSourceData.available_payment_methods ?? [];

  const {
    data: vccData,
    loading: isVCCLoading,
    error: vccError,
  } = useAPI<VirtualCardResponse[]>(getVirtualCard());

  const {
    data: stipends,
    loading: loadingStipends,
    error: stipendsError,
  } = useAPI<Payment[]>(paymentsService(), []);

  const latestPaymentMethod = useSelector(
    (state: RootState) => state.paymentMethods.data?.latest_payment_account
  ) as any;
  const vcc1 = useSelector<RootState, TransformedVccCardType | null>(
    (state) => state.vcc.vcc1Data
  );
  const vcc2 = useSelector<RootState, TransformedVccCardType | null>(
    (state) => state.vcc.vcc2Data
  );

  const { decryptedId: decryptedVcc1Value, decryptionLoading: decryptingVcc1 } =
    useDecrypt(vcc1?.target_account_identifier, [vcc1]);
  const { decryptedId: decryptedVcc2Value, decryptionLoading: decryptingVcc2 } =
    useDecrypt(vcc2?.target_account_identifier, [vcc2]);

  const currencySymbol = useSelector((state: RootState) =>
    selectCurrencySymbol(state)
  );

  const sumAmountFromStipends = React.useMemo(() => {
    return stipends?.reduce((accumulator, currentValue) => {
      return accumulator + currentValue.amount;
    }, 0);
  }, [stipends?.length]);

  const [isSettingVCAsPaymentAccount, settingVCAsPaymentAccount] =
    useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState<string>("");
  const [selectedMethod, setSelectedMethod] = useState<
    AvailablePaymentMethod | undefined
  >();

  // Unified loading state to prevent glitchy loading behavior
  const isAnyDataLoading = isVCCLoading || loadingStipends;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { showBoundary } = useErrorBoundary();

  useEffect(() => {
    if (!vccError) {
      return;
    }
    // if 404, the user doesn't have a vcc
    if (vccError.response?.status == 404) {
      return;
    }
    showBoundary(vccError);
  }, [vccError]);

  useEffect(() => {
    vccData?.forEach((vcc) => {
      const cardData: CardData = {
        target_account_id: vcc.id,
        card_number: vcc.card_number,
        balance: vcc.balance,
        type: PaymentType.VirtualCard,
        method_provider: getMethodProviderName(vcc.provider),
      };
      dispatch(setCardData(cardData));
    });
  }, [vccData]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPaymentType((event.target as HTMLInputElement).value);
    const method = paymentMethodsData.find(
      (method) =>
        method.method_provider_name === (event.target as HTMLInputElement).value
    );
    setSelectedMethod(method);
  };

  async function setVCCAsLatestPayment(provider: string) {
    try {
      const vccPaymentCreationData = await paymentService.createVccPayment(
        provider.toLowerCase()
      );
      dispatch(setLatestPaymentMethod(vccPaymentCreationData));
      dispatch(showToast(t("paymentMethod_updated")));
      settingVCAsPaymentAccount(false);
      navigate("/payments");
    } catch (err) {
      const errorDetails = getErrorDetails(err as AxiosError, t);
      if (errorDetails && errorDetails.type !== ErrorType.TERMS_UPDATED) {
        dispatch(
          showError({
            title: errorDetails.props.title,
            message: errorDetails.props.message,
          })
        );
      } else {
        showBoundary(err);
      }
      settingVCAsPaymentAccount(false);
    }
  }

  const handleContinueClick = async () => {
    if (!selectedMethod) {
      return;
    }

    if (
      selectedMethod.method_provider_name === VCC_PROVIDERS.checkbook &&
      vcc1
    ) {
      if (latestPaymentMethod.target_account_type !== PaymentType.VirtualCard) {
        const { balance, ...vccReqWithoutBalance } = vcc1;
        dispatch(setLatestPaymentMethod(vccReqWithoutBalance));
      }
      settingVCAsPaymentAccount(true);
      await setVCCAsLatestPayment(selectedMethod.provider);
    } else if (
      selectedMethod.method_provider_name === VCC_PROVIDERS.highnote &&
      vcc2
    ) {
      if (latestPaymentMethod.target_account_type !== PaymentType.VirtualCard) {
        const { balance, ...vccReqWithoutBalance } = vcc2;
        dispatch(setLatestPaymentMethod(vccReqWithoutBalance));
      }
      settingVCAsPaymentAccount(true);
      await setVCCAsLatestPayment(selectedMethod.provider);
    } else if (
      selectedMethod.method_provider_name === VCC_PROVIDERS.highnote &&
      !vcc2
    ) {
      navigateToCreateVC();
    } else {
      StorageManager.removeFormData();
      navigate(`/payment-methods/${selectedMethod.method_provider_name}`);
    }
  };

  function navigateToCreateVC() {
    navigate("/card-agreement");
  }

  function vccLabelSkeleton() {
    return (
      <span>
        <Skeleton
          variant="rounded"
          height={20}
          width={35}
          style={{ marginLeft: "10px" }}
        />
      </span>
    );
  }

  function paymentMethodLabel(method: any): React.ReactNode {
    if (method.method_provider_name === VCC_PROVIDERS.checkbook && vcc1) {
      return (
        <>
          {method.title + " "}
          {decryptedVcc1Value === null && vcc1?.target_account_id
            ? vccLabelSkeleton()
            : decryptedVcc1Value}
        </>
      );
    }

    if (method.method_provider_name === VCC_PROVIDERS.highnote && vcc2) {
      return (
        <>
          {method.title + " "}
          {decryptedVcc2Value === null && vcc2?.target_account_id
            ? vccLabelSkeleton()
            : decryptedVcc2Value}

          {/* <Chip sx={{ ml: 0.8 }} label="New" color="success" size="small" /> */}
        </>
      );
    }

    if (method.method_provider_name === VCC_PROVIDERS.highnote && !vcc2) {
      return (
        <>
          {method.title + " "}
          <Chip sx={{ ml: 0.8 }} label="New" color="success" size="small" />
        </>
      );
    }

    return method.title;
  }

  // Skeleton component for payment methods section
  const PaymentMethodsSkeleton = () => (
    <Grid
      container
      direction="column"
      borderRadius="4px"
      border="1px solid #e0e0e0"
      p={2}
      mb={2}
    >
      <Grid textAlign="left" item>
        <FormControl component="fieldset">
          <Box>
            {/* Skeleton for 2-3 payment method options */}
            {[1, 2].map((index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <Skeleton
                  variant="circular"
                  width={20}
                  height={20}
                  sx={{ marginRight: "12px" }}
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                  }}
                >
                  <Skeleton
                    variant="text"
                    width={120}
                    height={16}
                    sx={{ marginLeft: "8px" }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </FormControl>
      </Grid>
      <Grid item width="100%">
        <Box mt={3}>
          <Skeleton
            variant="rounded"
            height={48}
            width="100%"
            sx={{ borderRadius: "4px" }}
          />
        </Box>
      </Grid>
    </Grid>
  );

  const receivedStipendSince15March2024 =
    stipends &&
    stipends.length > 0 &&
    stipends.some(
      (stipend) =>
        stipend.action_on &&
        new Date(stipend.action_on) > new Date("2024-03-15")
    );

  function deprecatedPaymentAlert() {
    if (!vccData || !paymentMethodsSourceData) return null;

    const deprecationStatus = getDeprecatedPaymentMethodStatus(
      paymentMethodsSourceData,
      vccData
    );

    if (
      deprecationStatus ===
      DeprecatedPaymentMethodStatus.UPDATE_NEEDED_WITH_BALANCE
    ) {
      return (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          <Typography variant="body2" component="div">
            {t("deprecated_payment_update_needed_with_balance", {
              balance: formatCurrency(vcc1?.balance ?? 0),
            })}
          </Typography>
        </Alert>
      );
    }

    if (
      deprecationStatus ===
      DeprecatedPaymentMethodStatus.UPDATE_NEEDED_WITH_NO_BALANCE
    ) {
      return (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          <Typography variant="body2" component="div">
            {t("deprecated_payment_update_needed_no_balance")}
          </Typography>
        </Alert>
      );
    }

    return null;
  }

  return (
    <>
      {!latestPaymentMethod &&
        (isAnyDataLoading ? (
          <Skeleton
            variant="rounded"
            height={48}
            style={{ marginBottom: 10 }}
          />
        ) : (
          <Alert
            severity="info"
            sx={{
              backgroundColor: "#0588D1",
              marginBottom: "10px",
              fontWeight: "bold",
              color: "white",
            }}
          >
            {sumAmountFromStipends === 0
              ? t("paymentMethod_choose")
              : t("paymentMethod_amountWaiting", {
                  currency: currencySymbol,
                  amount: sumAmountFromStipends?.toFixed(2),
                })}
          </Alert>
        ))}

      {failedPayment && (
        <Alert
          severity="info"
          sx={{
            backgroundColor: "#0588D1",
            marginBottom: "10px",
            fontWeight: "bold",
            color: "white",
          }}
        >
          {t("paymentMethod_retry")}
        </Alert>
      )}

      {/* Deprecated Payment Method Alerts */}
      {!isAnyDataLoading && deprecatedPaymentAlert()}

      {isAnyDataLoading ? (
        <PaymentMethodsSkeleton />
      ) : (
        <Grid
          container
          direction="column"
          borderRadius="4px"
          border="1px solid grey"
          p={2}
          mb={2}
        >
          <Grid textAlign="left" item>
            <FormControl component="fieldset">
              <RadioGroup
                data-test-id="payment-type"
                name="payment-type"
                value={selectedPaymentType}
                onChange={handleChange}
              >
                {paymentMethodsData.map((method, index) => {
                  if (method.method_provider_name === VCC_PROVIDERS.checkbook) {
                    return null;
                  }

                  if (
                    client === "velocity" &&
                    !receivedStipendSince15March2024 &&
                    method.method_provider_name === VCC_PROVIDERS.highnote
                  ) {
                    return null;
                  }

                  return (
                    <FormControlLabel
                      sx={{ fontSize: "0.875rem" }}
                      key={index}
                      value={method.method_provider_name}
                      control={<Radio />}
                      label={
                        <Typography
                          data-test-id="payment-method-title"
                          fontSize="0.875rem"
                          style={{ display: "flex" }}
                        >
                          {paymentMethodLabel(method)}
                        </Typography>
                      }
                    />
                  );
                })}
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item width="100%">
            <Box mt={3}>
              <LoadingButton
                data-test-id="payment-methods-button"
                fullWidth
                size="large"
                variant="contained"
                disabled={!selectedMethod}
                onClick={handleContinueClick}
                loading={isSettingVCAsPaymentAccount}
                loadingPosition="start"
                startIcon={<WhiteSecurityIcon />}
              >
                {t("continue")}
              </LoadingButton>
            </Box>
          </Grid>
        </Grid>
      )}
      {/* {!isVCC2Loading && isVCC2PaymentMethodAvailable && !vcc2 && (
        <CreateVccCard />
      )} */}
    </>
  );
};

export default PaymentMethods;
